import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bot, Wrench, AlertTriangle, CheckCircle2, ArrowRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Compact "Bot Status" widget for embedding in pages like OneDrive.
 * Shows: current bot status, issue count, last run, and a 1-click
 * "Heal everything safe" button. Links to the full Pipeline Bot page.
 */
export default function PipelineBotWidget() {
  const [status, setStatus] = useState(null);
  const [diag, setDiag] = useState(null);
  const [healing, setHealing] = useState(false);

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const token = () => localStorage.getItem('saas_token') || localStorage.getItem('token') || '';

  const fetchAll = useCallback(async () => {
    try {
      const [st, di] = await Promise.all([
        fetch(`${apiBase}/api/pipeline-bot/status`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json()),
        fetch(`${apiBase}/api/pipeline-bot/diagnostics`, { headers: { Authorization: `Bearer ${token()}` } }).then(r => r.json()),
      ]);
      setStatus(st);
      setDiag(di);
    } catch (e) {
      // silent fail in widget
    }
  }, [apiBase]);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 20000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const onAutoHeal = async () => {
    setHealing(true);
    try {
      const r = await fetch(`${apiBase}/api/pipeline-bot/auto-heal-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
      }).then(r => r.json());
      const fixed = (r.actions || []).filter(a => a.fixed).length;
      toast.success(`Pipeline Bot applied ${fixed} safe fix(es)`);
      await fetchAll();
    } catch (e) {
      toast.error('Auto-heal failed');
    } finally {
      setHealing(false);
    }
  };

  if (!status) return null;

  const issues = diag?.issues || [];
  const overallStatus = status.status || 'healthy';

  const palette = {
    healthy:  { bg: 'bg-emerald-50 dark:bg-emerald-950/20', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500',  Icon: CheckCircle2 },
    degraded: { bg: 'bg-amber-50 dark:bg-amber-950/20',     border: 'border-amber-200',  text: 'text-amber-700',  dot: 'bg-amber-500',    Icon: AlertTriangle },
    critical: { bg: 'bg-red-50 dark:bg-red-950/20',         border: 'border-red-200',    text: 'text-red-700',    dot: 'bg-red-500',      Icon: AlertTriangle },
  }[overallStatus] || { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', dot: 'bg-slate-400', Icon: Bot };

  const StatusIcon = palette.Icon;

  return (
    <Card data-testid="pipeline-bot-widget" className={`${palette.bg} border ${palette.border}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`relative h-10 w-10 rounded-xl ${palette.dot} flex items-center justify-center shadow-md`}>
              <Bot className="h-5 w-5 text-white" />
              {status.is_enabled && (
                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">Pipeline Bot</span>
                <Badge variant="outline" className={`${palette.text} ${palette.border}`}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {issues.length === 0 ? 'All clear' : `${issues.length} issue${issues.length === 1 ? '' : 's'}`}
                </Badge>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {(status.stats?.total_auto_fixes ?? 0)} auto-fixes · runs every 60s
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {issues.length > 0 && (
              <Button
                data-testid="widget-auto-heal-btn"
                size="sm"
                variant="default"
                onClick={onAutoHeal}
                disabled={healing}
                className="bg-violet-600 hover:bg-violet-700"
              >
                {healing ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Wrench className="h-4 w-4 mr-1" />}
                Heal now
              </Button>
            )}
            <Link to="../pipeline-bot">
              <Button data-testid="widget-open-bot-btn" size="sm" variant="outline">
                Open Bot <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
